import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import type { SymbolsResponse } from '../../contracts/api'

/**
 * Колода символов одна на сетку и на карточку: «Символы → символ → назад»
 * иначе пересчитывает карты на каждый шаг. Держим ответ в модуле, как циклы и
 * расчёты (cyclesData, chartsData).
 */
let cache: SymbolsResponse | null = null

export function useSymbols(): { data: SymbolsResponse | null; error: string } {
  const [data, setData] = useState<SymbolsResponse | null>(cache)
  const [error, setError] = useState('')

  useEffect(() => {
    if (cache) return undefined
    let cancelled = false
    api
      .get<SymbolsResponse>('/api/symbols')
      .then((result) => {
        cache = result
        if (!cancelled) setData(result)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : 'Символы не открылись')
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { data, error }
}

/**
 * Какие символы человек уже открывал. Живёт в localStorage, а не на сервере:
 * это метка «я это видел», а не факт про карту — терять её не жалко, а лишний
 * столбец в базе ради бейджа заводить незачем. В приватном режиме хранилище
 * может бросать на любом обращении, поэтому обе функции молча переживают отказ.
 */
const SEEN_KEY = 'atlas.symbols.seen'

export function seenSymbols(): Set<string> {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((item): item is string => typeof item === 'string'))
  } catch {
    return new Set()
  }
}

export function markSymbolSeen(id: string): void {
  try {
    const seen = seenSymbols()
    if (seen.has(id)) return
    seen.add(id)
    window.localStorage.setItem(SEEN_KEY, JSON.stringify([...seen]))
  } catch {
    // приватный режим или переполненное хранилище — бейдж просто останется
  }
}
